import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import type { dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView$key as DateTimeReadViewFragment } from './__generated__/dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView.graphql';
import type { Props } from './types.tsx';

/**
 * DateTimeReadView component lets you view the date and time of a timestammp in the timeZone the current user is in.
 * In Case timeZone is passed as a prop in it adhears to prop timeZone
 *
 * We later need to fireUIAnalytics when an error occurs while formatting the date time
 * also discussions for removing timeZone is available here https://atlassian.slack.com/archives/CFG7SUPJM/p1665391419261039
 *
 * @param props {@link DateTimeReadView}
 * */
export const DateTimeReadView = (props: Props): string | React.JSX.Element => {
	const {
		fragmentRef,
		timeZone: overrideTimeZone,
		isDateOnlyFormat = false,
		shouldRenderHoverPopover = false,
	} = props;
	const data = useFragment<DateTimeReadViewFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView on JiraDateTimePickerField {
				id
				dateTime
			}
		`,
		fragmentRef,
	);
	const { dateTime } = data || {};
	const { formatDateTime, formatDateOnly } = useDateTimeFormatter(overrideTimeZone);

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data?.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();
	if (!dateTime) {
		return (
			<EmptyFieldText
				{...(isBespokeEmptyFieldTextGA && {
					placeholderText: messages.bespokenDateTimePlaceholderEmptyFieldText,
				})}
			/>
		);
	}

	let content;

	try {
		content = isDateOnlyFormat ? formatDateOnly(dateTime) : formatDateTime(dateTime);
	} catch (err) {
		return <></>;
	}

	if (shouldRenderHoverPopover && dateTime) {
		if (hoverPopoverRefFactory) {
			hoverPopoverRef = hoverPopoverRefFactory({
				content,
			});
		}

		return (
			<Text maxLines={1} ref={hoverPopoverRef}>
				{content}
			</Text>
		);
	}

	return content;
};
